/**
 * Race track scene — Canvas2D top-down with 3/4 view sprite horses.
 *
 * Visual flow:
 *   - Stadium background + dirt-textured lanes (track-draw.ts).
 *   - 8 horses with a 4-frame procedural gallop animation.
 *   - Dynamic commentary panel: pulls timed callouts from `commentary-feed.ts`.
 *   - Floating emoji cheers triggered by `hr_emoji_cheer` frames (visual only).
 *   - "Cheer on your horse!" banner along the bottom.
 *
 * Removed (per UX spec): the top-left countdown widget AND the top-right
 * race-time clock. The race itself has no visible timer — viewers just
 * watch the horses run. The race-number label stays.
 */

import type { TrackHorse, TrackState } from '../state/types.js';
import { drawHorseBody, drawLaneNumber, drawStaticTrack, type TrackLayout } from './track-draw.js';
import { buildCommentary, type CommentaryEvent } from './commentary-feed.js';
import { loadHorseSprites } from './horse-sprites.js';
import { sfx } from '../audio/sfx.js';

export interface TrackHandle {
  el: HTMLDivElement;
  setTrack(t: TrackState): void;
  setVisible(v: boolean): void;
  /**
   * Float an emoji up the lane corresponding to `lane` (1..8). Visual only.
   */
  pushCheer(lane: number, emoji: string): void;
  /** Called each animation frame from main.ts. */
  tick(): void;
}

const STAGE_W = 1920;
const STAGE_H = 1080;
const LAYOUT: TrackLayout = {
  trackX0: 200,
  trackX1: STAGE_W - 120,
  trackY0: 240,
  laneH: 92,
  numLanes: 8,
};

const CHEER_LIFESPAN_MS = 2_000;
const CHEER_RISE_PX = 220;
// Raise horse sprite Y by 50px so they sit higher in the lane (user feedback:
// horses were rendering too low against the track artwork). Applied at draw
// time only — the lane layout / track painting is unchanged so dividers,
// badges, and finish line still align with the underlying lane geometry.
const HORSE_Y_OFFSET = -50;

interface ActiveCheer {
  lane: number;
  emoji: string;
  bornAt: number; // performance.now() at spawn
  /** Horizontal jitter so multiple simultaneous cheers don't stack on top. */
  xJitter: number;
}

interface RenderState {
  current: TrackState | null;
  bobPhase: Map<number, number>;
  lastFrameMs: number;
  finishedAt: Map<number, number>;
  commentary: CommentaryEvent[];
  commentaryIdx: number;
  cheers: ActiveCheer[];
}

function lerpSeg(t: number, t0: number, t1: number, p0: number, p1: number): number {
  const span = t1 - t0;
  if (span <= 0) return p1;
  return p0 + (p1 - p0) * ((t - t0) / span);
}

function progressFor(h: TrackHorse, elapsedMs: number): number {
  if (elapsedMs <= 0) return 0;
  if (elapsedMs >= h.timeAtFinishMs) return 1;
  if (elapsedMs <= h.timeAt25Ms) return lerpSeg(elapsedMs, 0, h.timeAt25Ms, 0, 0.25);
  if (elapsedMs <= h.timeAt50Ms) return lerpSeg(elapsedMs, h.timeAt25Ms, h.timeAt50Ms, 0.25, 0.5);
  if (elapsedMs <= h.timeAt75Ms) return lerpSeg(elapsedMs, h.timeAt50Ms, h.timeAt75Ms, 0.5, 0.75);
  return lerpSeg(elapsedMs, h.timeAt75Ms, h.timeAtFinishMs, 0.75, 1);
}

function buildHeader(): { row: HTMLDivElement; raceLabel: HTMLDivElement } {
  const header = document.createElement('div');
  header.style.cssText = `
    position: absolute; top: 16px; left: 0; right: 0;
    display: flex; justify-content: center; align-items: center;
    padding: 0 32px; z-index: 5; pointer-events: none;
  `;
  const raceLabel = document.createElement('div');
  raceLabel.style.cssText = `
    font-family: 'Bebas Neue', 'Oswald', sans-serif;
    font-size: 56px; font-weight: 700;
    letter-spacing: 0.18em; color: #fff;
    text-shadow: 0 4px 0 rgba(0,0,0,0.6), 0 0 24px rgba(0,0,0,0.5);
  `;
  header.appendChild(raceLabel);
  return { row: header, raceLabel };
}

function buildCommentaryEl(): HTMLDivElement {
  const commentary = document.createElement('div');
  commentary.setAttribute('data-testid', 'hr-commentary');
  commentary.style.cssText = `
    position: absolute; top: 96px; left: 50%; transform: translateX(-50%);
    z-index: 5; pointer-events: none;
    background: linear-gradient(180deg, rgba(0,0,0,0.6), rgba(0,0,0,0.85));
    border: 2px solid var(--sg-primary, #FFC43D);
    border-radius: 999px;
    padding: 8px 32px;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 42px; letter-spacing: 0.16em;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 2px 0 rgba(0,0,0,0.6);
    box-shadow: 0 6px 24px rgba(0,0,0,0.6);
    transition: opacity 280ms ease;
    white-space: nowrap; max-width: 90%;
    overflow: hidden; text-overflow: ellipsis;
  `;
  commentary.textContent = '\u00a0';
  return commentary;
}

function buildCheerSign(): HTMLDivElement {
  const sign = document.createElement('div');
  sign.setAttribute('data-testid', 'hr-cheer-sign');
  sign.style.cssText = `
    position: absolute; bottom: 28px; left: 50%; transform: translateX(-50%);
    z-index: 6; pointer-events: none;
    display: flex; align-items: center; gap: 14px;
    background: linear-gradient(180deg, rgba(6,18,40,0.85), rgba(2,8,22,0.95));
    border: 2px solid var(--sg-accent, #00F0FF);
    border-radius: 999px;
    padding: 10px 26px;
    font-family: 'Oswald', sans-serif;
    font-size: 22px; letter-spacing: 0.20em;
    color: var(--sg-accent, #00F0FF);
    text-transform: uppercase;
    box-shadow: 0 8px 28px rgba(0,0,0,0.55), 0 0 22px rgba(0, 240, 255, 0.18);
  `;
  sign.innerHTML = `
    <span style="font-size: 28px;">💬</span>
    <span>Cheer on your horse with an emoji!</span>
  `;
  return sign;
}

function buildWinnerCallout(): HTMLDivElement {
  const winnerCallout = document.createElement('div');
  winnerCallout.style.cssText = `
    position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) scale(0.7);
    z-index: 8; pointer-events: none;
    background: linear-gradient(180deg, rgba(255,196,61,0.95), rgba(245,158,11,0.95));
    color: #1f1300;
    border: 4px solid #fff;
    border-radius: 24px;
    padding: 28px 56px;
    box-shadow: 0 16px 60px rgba(0,0,0,0.7), 0 0 80px rgba(255,196,61,0.4);
    text-align: center;
    opacity: 0;
    transition: opacity 320ms ease, transform 380ms cubic-bezier(0.34, 1.56, 0.64, 1);
  `;
  winnerCallout.innerHTML = `
    <div style="font-family:'Bebas Neue',sans-serif;font-size:48px;letter-spacing:0.32em;line-height:1;">WINNER</div>
    <div style="font-family:'Bebas Neue',sans-serif;font-size:96px;letter-spacing:0.04em;line-height:1.05;margin-top:10px;" data-role="winner-name"></div>
    <div style="font-family:'Oswald',sans-serif;font-size:32px;letter-spacing:0.2em;margin-top:10px;color:rgba(31,19,0,0.7);" data-role="winner-num"></div>
  `;
  return winnerCallout;
}

export function createTrack(stage: HTMLElement): TrackHandle {
  // Best-effort kick the sprite preload; once it resolves the next render
  // frame will pick up the bitmap path automatically. If the queued PNGs
  // don't exist yet, this resolves to null and procedural draw stays in.
  void loadHorseSprites();

  const el = document.createElement('div');
  el.setAttribute('data-scene', 'track');
  el.setAttribute('data-testid', 'hr-track');
  el.style.cssText = `
    position: absolute; inset: 0;
    background: #0a0e27;
    color: #fff;
    font-family: 'Inter', system-ui, sans-serif;
    z-index: 2;
    overflow: hidden;
  `;
  stage.appendChild(el);

  const { row: header, raceLabel } = buildHeader();
  el.appendChild(header);
  const commentaryEl = buildCommentaryEl();
  el.appendChild(commentaryEl);
  const winnerCallout = buildWinnerCallout();
  el.appendChild(winnerCallout);
  const cheerSign = buildCheerSign();
  el.appendChild(cheerSign);

  const canvas = document.createElement('canvas');
  canvas.width = STAGE_W;
  canvas.height = STAGE_H;
  canvas.style.cssText = `
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    pointer-events: none;
  `;
  el.appendChild(canvas);
  const ctx = canvas.getContext('2d')!;

  const state: RenderState = {
    current: null,
    bobPhase: new Map(),
    lastFrameMs: performance.now(),
    finishedAt: new Map(),
    commentary: [],
    commentaryIdx: 0,
    cheers: [],
  };

  function showWinnerCallout(name: string, num: number): void {
    const nameEl = winnerCallout.querySelector<HTMLDivElement>('[data-role="winner-name"]');
    const numEl = winnerCallout.querySelector<HTMLDivElement>('[data-role="winner-num"]');
    if (nameEl) nameEl.textContent = name;
    if (numEl) numEl.textContent = `#${num}`;
    winnerCallout.style.opacity = '1';
    winnerCallout.style.transform = 'translate(-50%, -50%) scale(1)';
  }

  function hideWinnerCallout(): void {
    winnerCallout.style.opacity = '0';
    winnerCallout.style.transform = 'translate(-50%, -50%) scale(0.7)';
  }

  function laneCenterY(lane: number): number {
    return LAYOUT.trackY0 + (lane - 1) * LAYOUT.laneH + LAYOUT.laneH / 2;
  }

  function drawCheers(tNow: number): void {
    if (state.cheers.length === 0) return;
    // Drop expired cheers in-place.
    state.cheers = state.cheers.filter((c) => tNow - c.bornAt < CHEER_LIFESPAN_MS);
    for (const c of state.cheers) {
      const t = (tNow - c.bornAt) / CHEER_LIFESPAN_MS; // 0..1
      const yBase = laneCenterY(c.lane);
      const y = yBase - CHEER_RISE_PX * t;
      // Emoji float comes from approximately under the horse's lane bottom edge.
      const x = STAGE_W / 2 + c.xJitter; // center jitter (decorative)
      ctx.save();
      ctx.globalAlpha = Math.max(0, 1 - t);
      ctx.font = '52px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 4;
      ctx.fillText(c.emoji, x, y);
      ctx.restore();
    }
  }

  function renderFrame(): void {
    if (!state.current) return;
    const tNow = performance.now();
    const dt = Math.min(0.05, (tNow - state.lastFrameMs) / 1000);
    state.lastFrameMs = tNow;
    const elapsed = Date.now() - state.current.startAtMs;

    ctx.clearRect(0, 0, STAGE_W, STAGE_H);
    drawStaticTrack(ctx, LAYOUT);

    const horseRows = state.current.horses.map((h) => {
      const p = progressFor(h, elapsed);
      const x = LAYOUT.trackX0 + (LAYOUT.trackX1 - LAYOUT.trackX0) * p;
      const yCenter = LAYOUT.trackY0 + (h.number - 1) * LAYOUT.laneH + LAYOUT.laneH / 2 + HORSE_Y_OFFSET;
      const finished = elapsed >= h.timeAtFinishMs;
      // Run row has 6 frames; 12fps target -> 12/6 = 2.0 cycles/sec.
      // Idle row has 11 frames; 6fps post-finish -> 6/11 ≈ 0.55 cycles/sec.
      const speed = finished ? 0.55 : 2.0;
      const cur = state.bobPhase.get(h.number) ?? Math.random();
      const next = (cur + speed * dt) % 1;
      state.bobPhase.set(h.number, next);
      if (finished && !state.finishedAt.has(h.number)) {
        state.finishedAt.set(h.number, tNow);
        // Fade THIS horse's gallop loop the moment it crosses the line —
        // herd thins audibly until the leader's cheer takes over.
        sfx.stopGallopLoopFor(h.number, 350);
      }
      return { h, x, y: yCenter, p, bob: next, finished };
    });
    horseRows.sort((a, b) => a.p - b.p);
    // Two-pass: bodies first, then lane numbers. Single-pass drew each
    // (body + number) in turn, so the next horse's body covered the previous
    // horse's number. Splitting passes guarantees numbers always sit on top
    // of every horse sprite, no matter the lane order.
    for (const row of horseRows) {
      drawHorseBody(ctx, { h: row.h, x: row.x, y: row.y, bobPhase: row.bob, finished: row.finished });
    }
    for (const row of horseRows) {
      drawLaneNumber(ctx, { h: row.h, x: row.x, y: row.y, bobPhase: row.bob, finished: row.finished });
    }

    // Commentary: walk the precomputed event list, swapping the displayed
    // line as we cross each `atMs` threshold.
    while (
      state.commentaryIdx < state.commentary.length
      && (state.commentary[state.commentaryIdx]?.atMs ?? Infinity) <= elapsed
    ) {
      const evt = state.commentary[state.commentaryIdx]!;
      if (commentaryEl.textContent !== evt.line) commentaryEl.textContent = evt.line;
      state.commentaryIdx++;
    }

    drawCheers(tNow);

    // Race-number label only — no race-time clock.
    const racesTotal = state.current.racesTotal;
    raceLabel.textContent = racesTotal > 0
      ? `RACE ${state.current.raceIndex + 1} OF ${racesTotal}`
      : `RACE #${state.current.raceIndex + 1}`;

    // Winner callout once the leader crosses the line and a small grace passes.
    const winner = state.current.horses.find((h) => h.finishPlace === 1);
    if (winner && state.finishedAt.has(winner.number)) {
      const since = tNow - (state.finishedAt.get(winner.number) ?? tNow);
      if (since > 350) {
        showWinnerCallout(winner.name, winner.number);
      }
    }
  }

  return {
    el,
    setTrack(t) {
      state.current = t;
      state.bobPhase.clear();
      state.finishedAt.clear();
      state.lastFrameMs = performance.now();
      state.cheers = [];
      hideWinnerCallout();
      commentaryEl.textContent = '\u00a0';
      // One gallop loop per horse — each fades out individually as its horse
      // crosses the finish line (renderFrame side-effect). Skip when horses
      // are parked (countdown seed: timeAtFinishMs is the sentinel future).
      const parked = t.horses.every((h) => h.timeAtFinishMs >= 900_000);
      if (!parked) {
        for (const h of t.horses) {
          sfx.startGallopLoopFor(h.number, 0.2, 250);
        }
      }
      // Pre-build the commentary feed once per race (deterministic from raceId).
      state.commentary = buildCommentary({
        raceId: t.raceId,
        raceIndex: t.raceIndex,
        racesTotal: t.racesTotal,
        horses: t.horses,
        raceDurationMs: t.raceDurationMs,
      });
      state.commentaryIdx = 0;
      renderFrame();
    },
    setVisible(v) {
      el.style.display = v ? 'block' : 'none';
      if (!v) hideWinnerCallout();
    },
    pushCheer(lane, emoji) {
      // Cap concurrent floats to keep the canvas readable.
      if (state.cheers.length > 30) state.cheers.shift();
      state.cheers.push({
        lane,
        emoji,
        bornAt: performance.now(),
        // Per-cheer horizontal jitter so multiple at once don't stack.
        xJitter: (Math.random() - 0.5) * 240,
      });
    },
    tick() {
      if (el.style.display !== 'none') renderFrame();
    },
  };
}
